import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  doc,
  getDoc,
  updateDoc,
  addDoc,
  collection,
  onSnapshot,
  serverTimestamp,
  deleteField
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage, ensureAuthTokenReady } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import ServiceEntryGrid from '../../components/ServiceEntryGrid';
import ServicesPreview from '../../components/ServicesPreview';
import AutocompleteInput from '../../components/AutocompleteInput';
import { fetchMasterCategories, fetchMasterServices } from '../../utils/masterCatalog';
import {
  rowsFromServiceCategories,
  buildServiceCategoriesFromRows,
  validateRows,
  findDuplicateRowErrors,
  groupRowsByCategory,
  isRowEmpty,
  makeEmptyCombo,
  combosFromServicesData,
  buildCombosForPublish,
  validateCombos,
  validateGenderMatch
} from '../../utils/serviceRows';
import './SalonServices.css';

const LOCKED_COPY = {
  pending: "Your service update request is waiting for Ulloor Styles to accept it.",
  accepted: "Ulloor Styles is preparing your service catalog. You'll be notified once it's ready to review."
};

// Strip enabled flags and drop anything the salon disabled, right before publish.
function filterEnabledForPublish(categories) {
  return (categories || [])
    .map((cat) => ({
      ...cat,
      services: (cat.services || [])
        .filter((svc) => svc.enabled !== false)
        .map((svc) => {
          const { enabled, ...rest } = svc;
          if (Array.isArray(svc.subServices) && svc.subServices.length) {
            const subServices = svc.subServices
              .filter((sub) => sub.enabled !== false)
              .map(({ enabled: subEnabled, ...subRest }) => subRest);
            return { ...rest, subServices };
          }
          return rest;
        })
        .filter((svc) => !Array.isArray(svc.subServices) || svc.subServices.length > 0)
    }))
    .filter((cat) => (cat.services || []).length > 0);
}

// Defensive default — the contract stamps enabled:true on write, but tolerate its absence.
const withEnabledDefault = (categories) =>
  (categories || []).map((cat) => ({
    ...cat,
    services: (cat.services || []).map((svc) =>
      Array.isArray(svc.subServices) && svc.subServices.length
        ? {
            ...svc,
            enabled: svc.enabled !== false,
            subServices: svc.subServices.map((sub) => ({ ...sub, enabled: sub.enabled !== false }))
          }
        : { ...svc, enabled: svc.enabled !== false }
    )
  }));

function SalonServices() {
  const navigate = useNavigate();
  const { salonData } = useAuth();
  const salonId = salonData?.salonId;

  const [salonDoc, setSalonDoc] = useState(null);
  const [docLoading, setDocLoading] = useState(true);

  const [masterCategories, setMasterCategories] = useState([]);
  const [masterServices, setMasterServices] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(true);

  const categoryOrder = useMemo(() => masterCategories.map((c) => c.name), [masterCategories]);

  // ----- Live salon doc (drives serviceCatalogStatus) -----
  useEffect(() => {
    if (!salonId) return undefined;
    const unsub = onSnapshot(
      doc(db, 'salons', salonId),
      (snap) => {
        setSalonDoc(snap.exists() ? snap.data() : null);
        setDocLoading(false);
      },
      (err) => {
        setDocLoading(false);
      }
    );
    return () => unsub();
  }, [salonId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [categories, services] = await Promise.all([fetchMasterCategories(), fetchMasterServices()]);
        if (!cancelled) {
          setMasterCategories(categories);
          setMasterServices(services);
        }
      } catch (err) {
      } finally {
        if (!cancelled) setCatalogLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const servicesFor = salonDoc?.profile?.servicesFor || 'Unisex'; // gates validateGenderMatch
  const status = salonDoc?.serviceCatalogStatus || 'not_requested';
  const isLocked = status === 'pending' || status === 'accepted';
  const isReview = status === 'awaiting_review';
  const isUnlocked = !isLocked && !isReview; // not_requested | published

  // ===================== Self-service editor (unlocked) =====================
  const gridRef = useRef(null);
  const [rows, setRows] = useState([]);
  const [combos, setCombos] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saveAttempted, setSaveAttempted] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [view, setView] = useState('edit');
  const [comboModal, setComboModal] = useState(null);
  const [comboSearch, setComboSearch] = useState('');
  const unlockedSeededRef = useRef(false);

  useEffect(() => {
    if (!salonDoc || catalogLoading) return;
    if (!isUnlocked) {
      unlockedSeededRef.current = false;
      return;
    }
    if (unlockedSeededRef.current) return;
    const servicesData = salonDoc.profile?.servicesData || {};
    setRows(groupRowsByCategory(rowsFromServiceCategories(servicesData.serviceCategories), categoryOrder));
    setCombos(combosFromServicesData(servicesData.combos));
    unlockedSeededRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salonDoc, catalogLoading, isUnlocked, categoryOrder]);

  useEffect(() => {
    if (saveAttempted) {
      setValidationErrors([...validateRows(rows), ...validateGenderMatch(rows, servicesFor), ...validateCombos(combos)]);
    }
  }, [rows, combos, servicesFor, saveAttempted]);

  const handleRowsChange = useCallback(
    (next) => setRows(groupRowsByCategory(next, categoryOrder)),
    [categoryOrder]
  );

  const liveDupErrors = useMemo(() => findDuplicateRowErrors(rows), [rows]);
  const displayErrors = saveAttempted ? validationErrors : liveDupErrors;
  const errorFields = useMemo(() => {
    const map = {};
    displayErrors.forEach((err) => {
      if (!err.rowId) return;
      if (!map[err.rowId]) map[err.rowId] = new Set();
      map[err.rowId].add(err.field);
    });
    return map;
  }, [displayErrors]);

  const previewCategories = useMemo(() => buildServiceCategoriesFromRows(rows, categoryOrder), [rows, categoryOrder]);
  const previewCombos = useMemo(() => buildCombosForPublish(combos), [combos]);

  const comboSearchOptions = useMemo(() => {
    const seen = new Set();
    const options = [];
    rows.forEach((r) => {
      if (isRowEmpty(r) || !(r.service || '').trim()) return;
      const name = `${r.service.trim()}${(r.subService || '').trim() ? ` - ${r.subService.trim()}` : ''}`;
      const key = name.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      options.push({
        id: r.id,
        name,
        nameLower: key,
        categoryName: r.category,
        gender: r.gender,
        defaultDuration: r.duration,
        price: Number(r.price) || null
      });
    });
    return options;
  }, [rows]);

  const handleSaveServices = async () => {
    setSaveAttempted(true);
    const errors = [...validateRows(rows), ...validateGenderMatch(rows, servicesFor), ...validateCombos(combos)];
    setValidationErrors(errors);
    if (errors.length) return;
    if (rows.every(isRowEmpty) && previewCombos.length === 0) {
      alert('Nothing to save — add at least one service first.');
      return;
    }
    try {
      setSaving(true);
      const salonRef = doc(db, 'salons', salonId);
      const existing = salonDoc?.profile?.servicesData || {};
      const publishedCategories = buildServiceCategoriesFromRows(rows, categoryOrder);
      const publishedCombos = buildCombosForPublish(combos);
      await updateDoc(salonRef, {
        'profile.servicesData': { ...existing, serviceCategories: publishedCategories, combos: publishedCombos },
        updatedAt: serverTimestamp()
      });
      alert('Your services have been saved. Customers now see the updated list.');
    } catch (err) {
      alert(`Failed to save: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const openComboModal = (combo) => {
    setComboSearch('');
    if (combo) {
      setComboModal({ combo: { ...combo, services: combo.services.map((s) => ({ ...s })) }, isNew: false });
    } else {
      setComboModal({ combo: makeEmptyCombo(), isNew: true });
    }
  };

  const updateModalCombo = (patch) => setComboModal((m) => ({ ...m, combo: { ...m.combo, ...patch } }));

  const addComboService = (name, price) => {
    const trimmed = (name || '').trim();
    if (!trimmed) return;
    setComboModal((m) => {
      const exists = m.combo.services.some((s) => s.name.toLowerCase() === trimmed.toLowerCase());
      if (exists) {
        alert(`"${trimmed}" is already included in this combo`);
        return m;
      }
      return { ...m, combo: { ...m.combo, services: [...m.combo.services, { name: trimmed, price: price ?? null }] } };
    });
    setComboSearch('');
  };

  const handleComboSave = () => {
    const combo = comboModal.combo;
    if (!(combo.title || '').trim()) {
      alert('Combo name is required');
      return;
    }
    if (combo.services.filter((s) => (s.name || '').trim()).length < 2) {
      alert('Include at least 2 services in the combo');
      return;
    }
    if (!(Number(combo.totalPrice) > 0)) {
      alert('Combo price must be greater than 0');
      return;
    }
    const duplicate = combos.find(
      (c) => c.id !== combo.id && (c.title || '').trim().toLowerCase() === combo.title.trim().toLowerCase()
    );
    if (duplicate) {
      alert(`A combo named "${duplicate.title}" already exists`);
      return;
    }
    setCombos((prev) => (comboModal.isNew ? [...prev, combo] : prev.map((c) => (c.id === combo.id ? combo : c))));
    setComboModal(null);
  };

  const handleComboDelete = (combo) => {
    if (!window.confirm(`Remove combo "${combo.title || 'Untitled'}"?`)) return;
    setCombos((prev) => prev.filter((c) => c.id !== combo.id));
  };

  // ----- Request Ulloor Styles to update services -----
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestFiles, setRequestFiles] = useState([]);
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [instagramUrl, setInstagramUrl] = useState('');
  const [requestNotes, setRequestNotes] = useState('');
  const [submittingRequest, setSubmittingRequest] = useState(false);

  const resetRequestForm = () => {
    setRequestFiles([]);
    setWebsiteUrl('');
    setInstagramUrl('');
    setRequestNotes('');
  };

  const canSubmitRequest =
    requestFiles.length > 0 || websiteUrl.trim() || instagramUrl.trim() || requestNotes.trim();

  const handleSubmitRequest = async () => {
    if (!canSubmitRequest) {
      alert('Add a menu file, website/Instagram link, or a note so Ulloor Styles knows what to prepare.');
      return;
    }
    try {
      setSubmittingRequest(true);
      const menuFileUrls = [];
      if (requestFiles.length) {
        await ensureAuthTokenReady();
        for (const file of requestFiles) {
          const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
          const storageRef = ref(storage, `salon_menu/${salonId}/${Date.now()}_${safeName}`);
          await uploadBytes(storageRef, file);
          const url = await getDownloadURL(storageRef);
          menuFileUrls.push(url);
        }
      }

      const reqRef = await addDoc(collection(db, 'serviceRequests'), {
        salonId,
        salonName: salonData?.profile?.shopName || 'Salon',
        type: 'update',
        status: 'pending',
        source: {
          menuFileUrls,
          websiteUrl: websiteUrl.trim() || null,
          instagramUrl: instagramUrl.trim() || null,
          notes: requestNotes.trim() || null
        },
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      await updateDoc(doc(db, 'salons', salonId), {
        serviceCatalogStatus: 'pending',
        activeServiceRequestId: reqRef.id
      });

      setShowRequestModal(false);
      resetRequestForm();
    } catch (err) {
      alert(`Failed to submit request: ${err.message}`);
    } finally {
      setSubmittingRequest(false);
    }
  };

  // ===================== Review (awaiting_review) =====================
  const [reviewCategories, setReviewCategories] = useState([]);
  const [reviewCombos, setReviewCombos] = useState([]);
  const [requestSummary, setRequestSummary] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const reviewSeededForRef = useRef(null);

  useEffect(() => {
    if (!salonDoc || status !== 'awaiting_review') return;
    const reqId = salonDoc.activeServiceRequestId || null;
    if (reviewSeededForRef.current === reqId) return;
    const pending = salonDoc.profile?.pendingServicesData || {};
    setReviewCategories(withEnabledDefault(pending.serviceCategories || []));
    setReviewCombos(pending.combos || []);
    reviewSeededForRef.current = reqId;
  }, [salonDoc, status]);

  useEffect(() => {
    const reqId = salonDoc?.activeServiceRequestId;
    if (!reqId || status !== 'awaiting_review') {
      setRequestSummary(null);
      return;
    }
    let cancelled = false;
    getDoc(doc(db, 'serviceRequests', reqId))
      .then((snap) => {
        if (!cancelled && snap.exists()) setRequestSummary(snap.data().summary || null);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [salonDoc?.activeServiceRequestId, status]);

  const toggleServiceEnabled = (catIdx, svcIdx, enabled) => {
    setReviewCategories((prev) =>
      prev.map((cat, ci) => {
        if (ci !== catIdx) return cat;
        return {
          ...cat,
          services: cat.services.map((svc, si) => {
            if (si !== svcIdx) return svc;
            if (Array.isArray(svc.subServices) && svc.subServices.length) {
              return { ...svc, enabled, subServices: svc.subServices.map((sub) => ({ ...sub, enabled })) };
            }
            return { ...svc, enabled };
          })
        };
      })
    );
  };

  const toggleSubServiceEnabled = (catIdx, svcIdx, subIdx, enabled) => {
    setReviewCategories((prev) =>
      prev.map((cat, ci) => {
        if (ci !== catIdx) return cat;
        return {
          ...cat,
          services: cat.services.map((svc, si) => {
            if (si !== svcIdx) return svc;
            return {
              ...svc,
              subServices: svc.subServices.map((sub, subi) => (subi === subIdx ? { ...sub, enabled } : sub))
            };
          })
        };
      })
    );
  };

  const updateServicePrice = (catIdx, svcIdx, price) => {
    setReviewCategories((prev) =>
      prev.map((cat, ci) => {
        if (ci !== catIdx) return cat;
        return { ...cat, services: cat.services.map((svc, si) => (si === svcIdx ? { ...svc, price } : svc)) };
      })
    );
  };

  const updateSubServicePrice = (catIdx, svcIdx, subIdx, price) => {
    setReviewCategories((prev) =>
      prev.map((cat, ci) => {
        if (ci !== catIdx) return cat;
        return {
          ...cat,
          services: cat.services.map((svc, si) => {
            if (si !== svcIdx) return svc;
            return { ...svc, subServices: svc.subServices.map((sub, subi) => (subi === subIdx ? { ...sub, price } : sub)) };
          })
        };
      })
    );
  };

  const handlePublishReview = async () => {
    const filtered = filterEnabledForPublish(reviewCategories);
    if (filtered.every((cat) => !cat.services.length) && reviewCombos.length === 0) {
      alert('Enable at least one service before publishing.');
      return;
    }
    if (!window.confirm('Publish this service list? Customers will see it immediately.')) return;

    try {
      setPublishing(true);
      const salonRef = doc(db, 'salons', salonId);
      await updateDoc(salonRef, {
        'profile.servicesData': {
          ...(salonDoc?.profile?.servicesData || {}),
          serviceCategories: filtered,
          combos: reviewCombos
        },
        'profile.pendingServicesData': deleteField(),
        serviceCatalogStatus: 'published',
        activeServiceRequestId: null,
        updatedAt: serverTimestamp()
      });

      const reqId = salonDoc?.activeServiceRequestId;
      if (reqId) {
        await updateDoc(doc(db, 'serviceRequests', reqId), {
          status: 'published',
          publishedAt: serverTimestamp()
        });
      }
      alert('Your services are now live!');
    } catch (err) {
      alert(`Failed to publish: ${err.message}`);
    } finally {
      setPublishing(false);
    }
  };

  // ===================== Render =====================
  if (!salonId || docLoading) {
    return (
      <div className="ssalon-page">
        <div className="ssalon-loading">
          <div className="ssalon-spinner"></div>
          <p>Loading your services...</p>
        </div>
      </div>
    );
  }

  if (isLocked) {
    return (
      <div className="ssalon-page">
        <div className="ssalon-header">
          <h2>Services &amp; Pricing</h2>
        </div>
        <div className="ssalon-locked-card">
          <div className="ssalon-locked-icon">{status === 'pending' ? '⏳' : '🛠️'}</div>
          <h3>{status === 'pending' ? 'Request Sent' : 'Being Prepared'}</h3>
          <p>{LOCKED_COPY[status]}</p>
        </div>
      </div>
    );
  }

  if (isReview) {
    return (
      <div className="ssalon-page">
        <div className="ssalon-header">
          <h2>Review Your Service Catalog</h2>
        </div>

        {requestSummary && (
          <div className="ssalon-summary-card">
            <p>Ulloor Styles prepared your catalog</p>
            <ul>
              <li>✓ Added {requestSummary.addedCount || 0} services</li>
              <li>✓ Updated {requestSummary.updatedCount || 0} services</li>
              <li>✓ Organized into {requestSummary.categoriesCount || 0} categories</li>
            </ul>
          </div>
        )}

        <div className="ssalon-review-hint">
          Turn off anything you don't want to offer, adjust prices if needed, then publish when you're ready.
          Customers won't see any of this until you publish.
        </div>

        <div className="ssalon-review-list">
          {reviewCategories.map((cat, catIdx) => (
            <div key={`${cat.name}-${catIdx}`} className="ssalon-review-category">
              <h3 className="ssalon-review-category-name">{cat.name}</h3>
              {(cat.services || []).map((svc, svcIdx) => (
                <div key={`${svc.name}-${svcIdx}`} className="ssalon-review-service-block">
                  {Array.isArray(svc.subServices) && svc.subServices.length ? (
                    <>
                      <label className="ssalon-review-row ssalon-review-parent-row">
                        <input
                          type="checkbox"
                          checked={svc.enabled !== false}
                          onChange={(e) => toggleServiceEnabled(catIdx, svcIdx, e.target.checked)}
                        />
                        <span className="ssalon-review-name">
                          {svc.name}
                          {svc.genderType && <span className="ssalon-review-gender">{svc.genderType}</span>}
                        </span>
                      </label>
                      <div className="ssalon-review-sublist">
                        {svc.subServices.map((sub, subIdx) => (
                          <label key={`${sub.name}-${subIdx}`} className="ssalon-review-row ssalon-review-sub-row">
                            <input
                              type="checkbox"
                              checked={sub.enabled !== false}
                              onChange={(e) => toggleSubServiceEnabled(catIdx, svcIdx, subIdx, e.target.checked)}
                            />
                            <span className="ssalon-review-name">{sub.name}</span>
                            <span className="ssalon-review-duration">{sub.duration ? `${sub.duration} mins` : ''}</span>
                            <span className="ssalon-review-price-wrap">
                              ₹
                              <input
                                type="number"
                                min="0"
                                className="ssalon-review-price-input"
                                value={sub.price ?? ''}
                                disabled={sub.enabled === false}
                                onChange={(e) =>
                                  updateSubServicePrice(
                                    catIdx,
                                    svcIdx,
                                    subIdx,
                                    e.target.value === '' ? 0 : Number(e.target.value)
                                  )
                                }
                              />
                            </span>
                          </label>
                        ))}
                      </div>
                    </>
                  ) : (
                    <label className="ssalon-review-row">
                      <input
                        type="checkbox"
                        checked={svc.enabled !== false}
                        onChange={(e) => toggleServiceEnabled(catIdx, svcIdx, e.target.checked)}
                      />
                      <span className="ssalon-review-name">
                        {svc.name}
                        {svc.genderType && <span className="ssalon-review-gender">{svc.genderType}</span>}
                      </span>
                      <span className="ssalon-review-duration">{svc.duration ? `${svc.duration} mins` : ''}</span>
                      <span className="ssalon-review-price-wrap">
                        ₹
                        <input
                          type="number"
                          min="0"
                          className="ssalon-review-price-input"
                          value={svc.price ?? ''}
                          disabled={svc.enabled === false}
                          onChange={(e) =>
                            updateServicePrice(catIdx, svcIdx, e.target.value === '' ? 0 : Number(e.target.value))
                          }
                        />
                      </span>
                    </label>
                  )}
                </div>
              ))}
            </div>
          ))}
          {reviewCategories.length === 0 && <p className="ssalon-review-empty">No services in this draft.</p>}
        </div>

        {reviewCombos.length > 0 && (
          <div className="ssalon-review-category">
            <h3 className="ssalon-review-category-name">✨ Combo Offers</h3>
            {reviewCombos.map((combo, i) => (
              <div key={`${combo.title}-${i}`} className="ssalon-review-combo-row">
                <span className="ssalon-review-name">{combo.title}</span>
                <span className="ssalon-review-combo-includes">
                  {(combo.services || []).map((s) => s.name).join(' + ')}
                </span>
                <span>₹{combo.totalPrice}</span>
              </div>
            ))}
          </div>
        )}

        <div className="ssalon-review-footer">
          <button className="ssalon-publish-btn" onClick={handlePublishReview} disabled={publishing}>
            {publishing ? 'Publishing...' : 'Publish'}
          </button>
        </div>
      </div>
    );
  }

  // ----- Unlocked: full self-service editor -----
  return (
    <div className="ssalon-page">
      <div className="ssalon-header">
        <div>
          <h2>Services &amp; Pricing</h2>
          <p className="ssalon-subtitle">Manage what customers see and book</p>
        </div>
        <div className="ssalon-header-actions">
          <div className="ssalon-view-toggle">
            <button className={`tab-btn ${view === 'edit' ? 'active' : ''}`} onClick={() => setView('edit')}>
              ✏️ Edit
            </button>
            <button className={`tab-btn ${view === 'preview' ? 'active' : ''}`} onClick={() => setView('preview')}>
              👁 Preview
            </button>
          </div>
          <button className="ssalon-request-btn" onClick={() => setShowRequestModal(true)}>
            📨 Request Ulloor Styles to update your services
          </button>
          <button className="ssalon-request-btn" onClick={() => navigate('/salon/offers')}>
            🏷️ Manage Offers
          </button>
          <button className="ssalon-save-btn" onClick={handleSaveServices} disabled={saving || catalogLoading}>
            {saving ? 'Saving...' : '✓ Save Services'}
          </button>
        </div>
      </div>

      {displayErrors.length > 0 && (
        <div className="ssalon-errors-panel">
          <h4>
            {saveAttempted
              ? `Fix ${displayErrors.length} issue${displayErrors.length === 1 ? '' : 's'} before saving:`
              : `${displayErrors.length} duplicate row${displayErrors.length === 1 ? '' : 's'} found:`}
          </h4>
          <ul>
            {displayErrors.slice(0, 10).map((err, i) => (
              <li key={`${err.rowId}-${err.field}-${i}`}>
                <button
                  className="ssalon-error-link"
                  onClick={() => {
                    setView('edit');
                    if (err.comboId) {
                      const combo = combos.find((c) => c.id === err.comboId);
                      if (combo) openComboModal(combo);
                    } else {
                      gridRef.current?.focusCell(err.rowId, err.field);
                    }
                  }}
                >
                  {err.message}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div style={{ display: view === 'edit' ? 'block' : 'none' }}>
        <ServiceEntryGrid
          ref={gridRef}
          rows={rows}
          onRowsChange={handleRowsChange}
          masterServices={masterServices}
          masterCategories={masterCategories}
          errorFields={errorFields}
        />

        <div className="ssalon-combos-section">
          <div className="ssalon-combos-header">
            <h3>Combos ({combos.length})</h3>
            <button className="ssalon-add-combo-btn" onClick={() => openComboModal(null)}>+ Add Combo</button>
          </div>
          {combos.length === 0 ? (
            <p className="ssalon-combos-empty">
              No combos yet — bundle services at a package price (e.g. Haircut + Beard Trim + Facial).
            </p>
          ) : (
            <div className="ssalon-combo-cards">
              {combos.map((combo) => (
                <div key={combo.id} className="ssalon-combo-card">
                  <div className="ssalon-combo-card-main">
                    <span className="ssalon-combo-title">{combo.title || 'Untitled combo'}</span>
                    <span className="ssalon-combo-services">
                      {(combo.services || []).map((s) => s.name).filter(Boolean).join(' + ') || 'No services yet'}
                    </span>
                  </div>
                  <span className="ssalon-combo-price">
                    {Number(combo.totalPrice) > 0 ? `₹${combo.totalPrice}` : '—'}
                  </span>
                  <div className="ssalon-combo-actions">
                    <button className="ssalon-combo-edit-btn" onClick={() => openComboModal(combo)}>Edit</button>
                    <button className="ssalon-combo-delete-btn" onClick={() => handleComboDelete(combo)}>×</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <div style={{ display: view === 'preview' ? 'block' : 'none' }}>
        <ServicesPreview serviceCategories={previewCategories} combos={previewCombos} />
      </div>

      {/* Combo add/edit modal */}
      {comboModal && (
        <div className="modal-overlay" onClick={() => setComboModal(null)}>
          <div className="modal-content ssalon-combo-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{comboModal.isNew ? 'Add Combo' : 'Edit Combo'}</h3>
              <button className="modal-close" onClick={() => setComboModal(null)}>×</button>
            </div>
            <div className="modal-body">
              <div className="ssalon-modal-row">
                <div className="ssalon-modal-field ssalon-combo-name-field">
                  <label>Combo Name *</label>
                  <input
                    type="text"
                    className="ssalon-modal-input"
                    placeholder="e.g. Grooming Package"
                    value={comboModal.combo.title}
                    onChange={(e) => updateModalCombo({ title: e.target.value })}
                    autoFocus
                  />
                </div>
                <div className="ssalon-modal-field">
                  <label>Combo Price (₹) *</label>
                  <input
                    type="number"
                    min="0"
                    className="ssalon-modal-input"
                    placeholder="0"
                    value={comboModal.combo.totalPrice ?? ''}
                    onChange={(e) =>
                      updateModalCombo({ totalPrice: e.target.value === '' ? null : Number(e.target.value) })
                    }
                  />
                </div>
              </div>

              <label>Included Services * (minimum 2)</label>
              <AutocompleteInput
                className="ssalon-modal-input"
                value={comboSearch}
                placeholder="🔍 Search your services..."
                options={comboSearchOptions}
                createLabelText="as a custom item"
                onChange={setComboSearch}
                onSelect={(opt) => addComboService(opt.name, opt.price)}
                onCreateNew={(text) => addComboService(text, null)}
              />

              {comboModal.combo.services.length === 0 ? (
                <p className="ssalon-modal-hint">
                  Search and select from the services entered above, or type any name and add it as a custom item.
                </p>
              ) : (
                <div className="ssalon-combo-included-list">
                  {comboModal.combo.services.map((svc, index) => (
                    <div key={`${svc.name}-${index}`} className="ssalon-combo-included-row">
                      <span className="ssalon-combo-included-name">{svc.name}</span>
                      <input
                        type="number"
                        min="0"
                        className="ssalon-combo-included-price"
                        placeholder="₹ (optional)"
                        value={svc.price ?? ''}
                        onChange={(e) =>
                          updateModalCombo({
                            services: comboModal.combo.services.map((s, i) =>
                              i === index ? { ...s, price: e.target.value === '' ? null : Number(e.target.value) } : s
                            )
                          })
                        }
                      />
                      <button
                        className="ssalon-combo-included-remove"
                        onClick={() =>
                          updateModalCombo({ services: comboModal.combo.services.filter((_, i) => i !== index) })
                        }
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="modal-cancel-btn" onClick={() => setComboModal(null)}>Cancel</button>
              <button
                className="ssalon-save-btn"
                onClick={handleComboSave}
                disabled={!comboModal.combo.title.trim() || comboModal.combo.services.length < 2}
              >
                {comboModal.isNew ? 'Add Combo' : 'Save Combo'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Request Ulloor Styles modal */}
      {showRequestModal && (
        <div className="modal-overlay" onClick={() => !submittingRequest && setShowRequestModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Request Ulloor Styles to update your services</h3>
              <button
                className="modal-close"
                onClick={() => setShowRequestModal(false)}
                disabled={submittingRequest}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p className="ssalon-modal-hint">
                Share your price menu or website/Instagram and our team will prepare a categorized service
                catalog for you to review and publish.
              </p>
              <label>Menu Image / PDF</label>
              <input
                type="file"
                accept="image/*,application/pdf"
                multiple
                onChange={(e) => setRequestFiles(Array.from(e.target.files || []))}
                disabled={submittingRequest}
              />
              {requestFiles.length > 0 && (
                <p className="ssalon-modal-hint">{requestFiles.length} file(s) selected</p>
              )}

              <label>Website URL</label>
              <input
                type="text"
                className="ssalon-modal-input"
                placeholder="https://..."
                value={websiteUrl}
                onChange={(e) => setWebsiteUrl(e.target.value)}
                disabled={submittingRequest}
              />

              <label>Instagram URL</label>
              <input
                type="text"
                className="ssalon-modal-input"
                placeholder="https://instagram.com/..."
                value={instagramUrl}
                onChange={(e) => setInstagramUrl(e.target.value)}
                disabled={submittingRequest}
              />

              <label>Notes (optional)</label>
              <textarea
                className="ssalon-modal-input"
                rows={3}
                placeholder="Anything else we should know..."
                value={requestNotes}
                onChange={(e) => setRequestNotes(e.target.value)}
                disabled={submittingRequest}
              />
            </div>
            <div className="modal-footer">
              <button
                className="modal-cancel-btn"
                onClick={() => setShowRequestModal(false)}
                disabled={submittingRequest}
              >
                Cancel
              </button>
              <button
                className="ssalon-save-btn"
                onClick={handleSubmitRequest}
                disabled={submittingRequest || !canSubmitRequest}
              >
                {submittingRequest ? 'Submitting...' : 'Submit Request'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SalonServices;
